import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { PageLoader } from '@ui/components/ui';
import { useMeQuery } from '@ui/store/authApi';
import type { Session } from '@ui/store/types';

/** Leaves this app for a full URL (the other app's domain), or navigates within it. */
export function Redirect({ to }: { to: string }) {
  const external = /^https?:\/\//.test(to);
  useEffect(() => {
    if (external) window.location.replace(to);
  }, [to, external]);
  return external ? <PageLoader /> : <Navigate to={to} replace />;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { data, isLoading, isError } = useMeQuery();
  const location = useLocation();
  if (isLoading) return <div className="flex h-screen items-center justify-center"><PageLoader /></div>;
  if (isError || !data) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <>{children}</>;
}

/** Sign-in screens: someone already signed in goes where `home` says. */
export function PublicOnly({ children, home }: { children: ReactNode; home: (session: Session) => string }) {
  const { data, isLoading } = useMeQuery();
  if (isLoading) return null;
  if (data) return <Redirect to={home(data)} />;
  return <>{children}</>;
}
